import {ReactElement, useContext} from "react"
import {ItemWarehouse} from "./api/itemwarehouse"
import {ProjectedStock} from "./projectedstock"
import { Link } from "react-router-dom";
import { useDivision } from "./shared"
import {AllDataContext} from "./LoggedinMainPage"

type Props = {
  itemWarehouse: ItemWarehouse | {itemCode: string, warehouseCode: string}
  infoLevel: number
  warningLevel: number
  negativeToOOS: boolean
  showCounts: boolean
  comment: string
}

export function getAvailble(
  projectedAvailableStock: ProjectedStock,
  infoLevel: number,
): string {
  if (projectedAvailableStock.currentAvailableStock >= infoLevel) {
    return "Full"
  }
  const pasAboveWarning =
    Array.from(projectedAvailableStock.projection.entries())
  .find(([_date, info]) =>
        info.eodProjectedAvailableStock !== undefined &&
        info.eodProjectedAvailableStock >= infoLevel)
  if (!pasAboveWarning) {
    return "Further notice"
  }
  return pasAboveWarning[0].toISOWeekString()
}

function round(nr: number | undefined, decimals: number): number | undefined {
  if (nr === undefined) {
    return undefined
  }
  return Math.round(nr * Math.pow(10, decimals)) / Math.pow(10, decimals)
}

export function getLevelAndCustomLevelMessage(
  stock: number | undefined,
  warningLevel: number,
  infoLevel: number,
  negativeToOOS: boolean,
  showCounts: boolean,
): ["loading" | "error" | "out-of-stock" | "warning" | "info" | "ok", ReactElement] {
  const level = (
    stock === undefined ? "loading"
    : stock <0 ? (negativeToOOS ? "out-of-stock" : "error")
    : stock === 0 ? "out-of-stock"
    : stock < warningLevel ? "warning"
    : stock < infoLevel ? "info"
    : "ok"
  )

  const usesCustomLevel = (
    level === "error"
    || (warningLevel !== ItemWarehouse.DEFAULT_WARNING_STOCK_LEVEL
        && (level === "info" || level === "warning"))
    || (infoLevel !== ItemWarehouse.DEFAULT_INFO_STOCK_LEVEL
        && (level === "info" || level === "ok")))

  const customLevelMessage =
    showCounts ? <span>{round(stock, 2)}</span> :
    !usesCustomLevel ? <>&nbsp;</> :
    level === "error" ?
      <span>Error: {round(stock, 2)}</span> :
    level === "ok" ? <span>More than {infoLevel}</span> :
    level === "info" ? <span>{warningLevel} to {infoLevel}</span> :
    level === "warning" ? <span>Less than {warningLevel}</span>
      : <>&nbsp;</>

    return [level, customLevelMessage]
}

export function PASOverviewTableRow(
    {itemWarehouse, infoLevel, negativeToOOS, showCounts,
      warningLevel, comment}: Props
): ReactElement {
  const isItemWarehouse = itemWarehouse instanceof ItemWarehouse
  const division = useDivision()
  const allData = useContext(AllDataContext)

  const projectedAvailableStock: ProjectedStock | undefined =
    allData.projectedStocks.filter(
      ps => ps.warehouseCode === itemWarehouse.warehouseCode
      && ps.itemCode === itemWarehouse.itemCode)[0]

  const [level, customLevelMessage] = getLevelAndCustomLevelMessage(
    projectedAvailableStock === undefined ? undefined
      : projectedAvailableStock.currentAvailableStock, warningLevel, infoLevel,
      negativeToOOS, showCounts
  )

  const cardUrl = isItemWarehouse ? [
    "",
    "division",
    division,
    "PASDetailed",
     itemWarehouse.warehouseCode,
     itemWarehouse.itemCode,
     "0" + warningLevel.toString(),
     "0" + infoLevel.toString(),
  ].map(encodeURIComponent).join("/") : ""

  return <tr className="PASOverviewTableRow">
    {!isItemWarehouse ? <>
      <td>{itemWarehouse.itemCode}</td>
      <td className="unknown">Unknown item</td>
      <td className="unknown"></td>
      <td className="unknown"></td>
      <td className="unknown"></td>
      <td className="unknown"></td>
      <td className="unknown comment">{comment}</td>
    </>: <>
      <td className="ShowLinkOnlyOnHoverToAllowCopyToEmailWithoutTheLink">
        <Link to={cardUrl}>{itemWarehouse.itemCode}</Link>
        <span>{itemWarehouse.itemCode}</span>
      </td>
      <td className="ShowLinkOnlyOnHoverToAllowCopyToEmailWithoutTheLink">
        <Link to={cardUrl}>{itemWarehouse.itemDescription}</Link>
        <span>{itemWarehouse.itemDescription}</span>
      </td>
      <td />
      <td className={level}>{customLevelMessage}</td>
      <td />
      {projectedAvailableStock === undefined ? <td className="loading" /> :
        <td>{getAvailble(projectedAvailableStock, infoLevel)}</td>
      }
      <td className="comment">{comment}</td>
    </>
    }
  </tr>
}
